import { logger } from "./logger";

type ClearFn = () => void | Promise<void>;

const callbacks = new Set<ClearFn>();

/**
 * Register a callback that wipes a module's local data on logout.
 * Call at module load so the registration is in place before logout runs.
 */
export const registerLocalDataCleanup = (fn: ClearFn): void => {
  callbacks.add(fn);
};

/** Indexed DB database name shared by every CRUD store via syncedCrudPersist. */
const INDEXED_DB_NAME = "index";

/** Name of the BroadcastChannel used to coordinate logout across tabs. */
const LOGOUT_CHANNEL_NAME = "index-logout";

/**
 * Cross-tab logout coordination. When one tab logs out it posts on this
 * channel; every other tab navigates to `/login`, which tears down the page
 * and releases the IndexedDB connections held by `syncedCrudPersist`. With
 * the other connections gone, the `deleteDatabase` call in this tab no
 * longer hits the `onblocked` path.
 */
const logoutChannel: BroadcastChannel | null =
  typeof BroadcastChannel === "undefined"
    ? null
    : new BroadcastChannel(LOGOUT_CHANNEL_NAME);

if (logoutChannel) {
  logoutChannel.onmessage = (event) => {
    if (event.data === "logout") {
      window.location.replace("/login");
    }
  };
}

/**
 * Run every registered cleanup. Errors are logged but never thrown so
 * logout always proceeds even if a single cleanup fails. Broadcasts a
 * logout signal to other tabs first so they release their IndexedDB
 * connections before the delete runs.
 */
export const clearAllLocalData = async (): Promise<void> => {
  try {
    logoutChannel?.postMessage("logout");
  } catch (err) {
    logger.error({ err }, "failed to broadcast logout to other tabs");
  }
  // Give other tabs a moment to receive the broadcast and unload, so they
  // release their IndexedDB connections before we try to delete the DB.
  if (logoutChannel) {
    await new Promise((resolve) => setTimeout(resolve, 150));
  }
  const results = await Promise.allSettled(
    [...callbacks].map((fn) => Promise.resolve().then(fn)),
  );
  for (const result of results) {
    if (result.status === "rejected") {
      logger.error({ err: result.reason }, "local-data cleanup failed");
    }
  }
};

registerLocalDataCleanup(() => {
  try {
    localStorage.clear();
  } catch (err) {
    logger.error({ err }, "failed to clear localStorage on logout");
  }
});

registerLocalDataCleanup(
  () =>
    new Promise<void>((resolve) => {
      try {
        const req = indexedDB.deleteDatabase(INDEXED_DB_NAME);
        req.onsuccess = () => resolve();
        req.onerror = () => {
          logger.error(
            { err: req.error },
            "failed to delete IndexedDB on logout",
          );
          resolve();
        };
        req.onblocked = () => {
          // Another tab still holds a connection. Logout proceeds anyway,
          // and the broadcast above will navigate that tab to /login so
          // its connection is released shortly. Not an error — the next
          // login on this tab will recreate the DB cleanly.
          logger.warn(
            "IndexedDB delete blocked on logout (another tab holds the connection); logout will proceed",
          );
          resolve();
        };
      } catch (err) {
        logger.error({ err }, "failed to schedule IndexedDB delete on logout");
        resolve();
      }
    }),
);
